import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { API_VERSION } from "@/lib/apiVersion";

export interface PressureRankingEntry {
  managerId: string;
  managerSlug: string | null;
  managerName: string;
  clubId: string;
  clubName: string;
  clubSlug: string | null;
  brandPrimary: string | null;
  brandSecondary: string | null;
  /** 0–100 scale */
  score: number;
  played: number;
  daysInRole: number;
  form: ("W" | "D" | "L")[];
  currentPosition: number | null;
  expectedPosition: number | null;
  positionVsExpectation: string | null;
  startDate: string | null;
}

export interface PressureRankingResult {
  entries: PressureRankingEntry[];
  lastUpdated: string | null;
  season: string | null;
}

/**
 * Current-manager pressure rankings for one league, highest MPI first.
 *
 * Shared by /sack-race and /league/:code so the public SEO pages and the
 * product surfaces never disagree about who is under pressure.
 */
export const usePressureRankings = (leagueCode: string | undefined) => {
  return useQuery({
    queryKey: ["pressure-rankings", leagueCode, API_VERSION],
    enabled: !!leagueCode,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PressureRankingResult> => {
      const code = leagueCode!;
      const today = new Date().toISOString().split("T")[0];

      // Current season roster for the league
      const { data: participants, error: partErr } = await supabase
        .from("season_participants")
        .select("season, club_id")
        .eq("league", code);
      if (partErr) throw partErr;

      let season: string | null = null;
      for (const p of participants || []) {
        if (!season || p.season > season) season = p.season;
      }
      const rosterIds = new Set(
        (participants || []).filter((p) => p.season === season).map((p) => p.club_id),
      );

      const { data: scores, error: scoresErr } = await supabase
        .from("manager_hot_seat_scores")
        .select(
          "manager_id, club_id, hot_seat_score, played, days_in_role, recent_form, current_position, expected_position, position_vs_expectation, last_calculated_at, managers(name, slug), clubs(name, slug, brand_primary, brand_secondary), manager_assignments!inner(start_date, end_date)",
        )
        .eq("league", code)
        .order("hot_seat_score", { ascending: false });
      if (scoresErr) throw scoresErr;

      // Only open tenures, at clubs on this season's roster, one row per club
      const bestByClub = new Map<string, any>();
      for (const row of scores || []) {
        const a = row.manager_assignments as any;
        if (!a) continue;
        if (a.end_date !== null && a.end_date < today) continue;
        if (rosterIds.size > 0 && !rosterIds.has(row.club_id)) continue;
        const existing = bestByClub.get(row.club_id);
        if (!existing || (a.start_date ?? "") > (existing.manager_assignments?.start_date ?? "")) {
          bestByClub.set(row.club_id, row);
        }
      }

      let lastUpdated: string | null = null;
      const entries: PressureRankingEntry[] = Array.from(bestByClub.values())
        .map((row: any) => {
          if (row.last_calculated_at && (!lastUpdated || row.last_calculated_at > lastUpdated)) {
            lastUpdated = row.last_calculated_at;
          }
          const raw = Number(row.hot_seat_score) || 0;
          return {
            managerId: row.manager_id,
            managerSlug: row.managers?.slug ?? null,
            managerName: row.managers?.name ?? "Unknown",
            clubId: row.club_id,
            clubName: row.clubs?.name ?? "",
            clubSlug: row.clubs?.slug ?? null,
            brandPrimary: row.clubs?.brand_primary ?? null,
            brandSecondary: row.clubs?.brand_secondary ?? null,
            score: Math.round(raw <= 1 ? raw * 100 : raw),
            played: row.played ?? 0,
            daysInRole: row.days_in_role ?? 0,
            form: row.recent_form
              ? (String(row.recent_form).split("") as ("W" | "D" | "L")[])
              : [],
            currentPosition: row.current_position ?? null,
            expectedPosition: row.expected_position ?? null,
            positionVsExpectation: row.position_vs_expectation ?? null,
            startDate: row.manager_assignments?.start_date ?? null,
          };
        })
        .sort((a, b) => b.score - a.score);

      return { entries, lastUpdated, season };
    },
  });
};

/** URL slug for a league code, e.g. "ENG_PL" -> "eng-pl" */
export const leagueCodeToSlug = (code: string) => code.toLowerCase().replace(/_/g, "-");

/** League code from a URL slug, e.g. "eng-pl" -> "ENG_PL" */
export const leagueSlugToCode = (slug: string) => slug.toUpperCase().replace(/-/g, "_");
